// Stores DTOs — BUILD_SPEC.md §5.8 (stores_issues/stores_issue_lines, transfers/
// transfer_lines), §8.6 (guided flows). Owned by G9 (plan.md) — this is the ONLY
// core file G9 fills; the barrel (schemas/index.ts) already re-exports it.
//
// Status values are the same three-state DRAFT|POSTED|REVERSED lifecycle every other
// posted document in the spec uses (weighbridge_tickets, goods_receipts, dispatches,
// ...). They are deliberately NOT sourced from `@zebra/core/enums.ts`: the S0 registry
// rule for this goal is "touch ONLY packages/db/src/schema/stores.ts (+rls),
// packages/core/src/schemas/stores.ts, services/api/src/modules/stores/, colocated
// tests" — enums.ts is G2's foundation file and is out of scope here. Flagged for the
// orchestrator: a future cleanup could fold these into enums.ts alongside
// TICKET_STATUSES/GOODS_RECEIPT_STATUSES/DISPATCH_STATUSES (which already use the
// identical ['DRAFT','POSTED','REVERSED'] tuple) once a goal is scoped to touch it.
import { z } from 'zod';
import { paginationRequestSchema, quantitySchema, ulidSchema } from './common.js';
/**
 * Reserved ledger reason — the ONLY reason string `record-movement.ts` uses for its
 * internal `wac-residual-flush` audit marker (booked with `source: 'DERIVED'` when an
 * outbound over-relief's zero-floor clamp discards information; see its doc). Two
 * consumers key off this exact reason to detect a real marker:
 *   - `fold.ts`'s `foldLedger` SKIPS the row (it must not double-count the flush).
 *   - `reversal.ts`'s `reverseDocument` REFUSES to reverse a document that booked one
 *     (a linear replay would fabricate the clamped value).
 * Both now require `source === 'DERIVED'` ALONGSIDE this reason (never on reason
 * alone) — `stockAdjustmentWriteSchema.reason` below is free text a user can type,
 * and prior to this fix an operator naming an ordinary write-off exactly
 * `wac-residual-flush` could get it silently skipped by the fold and permanently
 * blocked from reversal (adversarial gate finding, 2026-08-21). `recordMovement`
 * additionally rejects any CALLER-supplied reason equal to this constant outright
 * (services/api/src/ledger/record-movement.ts) — defense in depth so no future
 * caller, not just adjustments, can forge the marker.
 */
export const WAC_RESIDUAL_FLUSH_REASON = 'wac-residual-flush';
export const STORES_ISSUE_STATUSES = ['DRAFT', 'POSTED', 'REVERSED'];
export const TRANSFER_STATUSES = ['DRAFT', 'POSTED', 'REVERSED'];
/** Base UOM quantity, max 3dp, strictly positive — every stores/transfer line
 * carries the amount to move, always entered as a positive number; the posting
 * endpoint is responsible for choosing the signed direction it hands to
 * `recordMovement` (S1). */
const positiveQtySchema = z
    .string()
    .regex(/^\d+(\.\d{1,3})?$/, 'Invalid decimal quantity (max 3 decimal places)')
    .refine((v) => Number(v) > 0, 'qty must be greater than zero');
// ---------------------------------------------------------------------------
// Stores issues
// ---------------------------------------------------------------------------
export const storesIssueLineInputSchema = z.object({
    productId: ulidSchema,
    fromLocationId: ulidSchema,
    qty: positiveQtySchema,
});
export const storesIssueWriteSchema = z.object({
    siteId: ulidSchema,
    costCentreId: ulidSchema,
    issuedAt: z.coerce.date(),
    notes: z.string().nullable().optional(),
    lines: z.array(storesIssueLineInputSchema).min(1, 'At least one line is required'),
});
// Edit (PATCH, DRAFT-only) accepts the exact same shape as create — a full
// replace of the header + lines, matching "documents are freely editable in
// DRAFT" (CLAUDE.md conventions).
export const storesIssueUpdateSchema = storesIssueWriteSchema;
export const storesIssueLineResponseSchema = z.object({
    id: ulidSchema,
    lineNo: z.number().int().positive(),
    productId: ulidSchema,
    fromLocationId: ulidSchema,
    qty: z.string(),
});
export const storesIssueResponseSchema = z.object({
    id: ulidSchema,
    tenantId: ulidSchema,
    siteId: ulidSchema,
    number: z.string(),
    status: z.enum(STORES_ISSUE_STATUSES),
    issuedAt: z.coerce.date(),
    costCentreId: ulidSchema,
    notes: z.string().nullable(),
    lines: z.array(storesIssueLineResponseSchema),
});
export const storesIssueListQuerySchema = paginationRequestSchema;
/** One `recordMovement` result surfaced back to the caller as part of a posting
 * summary — `valueCents` transported as a decimal string (JSON has no bigint). */
export const storesIssueMovementSummarySchema = z.object({
    lineId: ulidSchema,
    productId: ulidSchema,
    qty: z.string(),
    valueCents: z.string(),
    movementId: ulidSchema,
});
export const storesIssuePostResponseSchema = z.object({
    issue: storesIssueResponseSchema,
    movements: z.array(storesIssueMovementSummarySchema),
    summary: z.string(),
});
export const storesIssueReverseResponseSchema = z.object({
    issue: storesIssueResponseSchema,
    reversedMovementIds: z.array(ulidSchema),
});
// ---------------------------------------------------------------------------
// Transfers
// ---------------------------------------------------------------------------
export const transferLineInputSchema = z.object({
    productId: ulidSchema,
    qty: positiveQtySchema,
});
export const transferWriteSchema = z.object({
    fromLocationId: ulidSchema,
    toLocationId: ulidSchema,
    fromSiteId: ulidSchema,
    toSiteId: ulidSchema,
    notes: z.string().nullable().optional(),
    lines: z.array(transferLineInputSchema).min(1, 'At least one line is required'),
});
export const transferUpdateSchema = transferWriteSchema;
export const transferLineResponseSchema = z.object({
    id: ulidSchema,
    lineNo: z.number().int().positive(),
    productId: ulidSchema,
    qty: z.string(),
});
export const transferResponseSchema = z.object({
    id: ulidSchema,
    tenantId: ulidSchema,
    number: z.string(),
    status: z.enum(TRANSFER_STATUSES),
    fromLocationId: ulidSchema,
    toLocationId: ulidSchema,
    fromSiteId: ulidSchema,
    toSiteId: ulidSchema,
    notes: z.string().nullable(),
    lines: z.array(transferLineResponseSchema),
});
export const transferListQuerySchema = paginationRequestSchema;
/** A transferred line books a TRANSFER_OUT/TRANSFER_IN pair (S1) — both movement
 * ids are surfaced so the client (and reversal machinery) can trace either leg. */
export const transferMovementSummarySchema = z.object({
    lineId: ulidSchema,
    productId: ulidSchema,
    qty: z.string(),
    valueCents: z.string(),
    outMovementId: ulidSchema,
    inMovementId: ulidSchema,
});
export const transferPostResponseSchema = z.object({
    transfer: transferResponseSchema,
    movements: z.array(transferMovementSummarySchema),
    summary: z.string(),
});
export const transferReverseResponseSchema = z.object({
    transfer: transferResponseSchema,
    reversedMovementIds: z.array(ulidSchema),
});
// ---------------------------------------------------------------------------
// Ad-hoc stock adjustments (G-A, Wave HOWICK-1, BUILD_SPEC.md §5.8/§8.6
// amendment 2026-08-21) — a first-class "write off / correct stock" document,
// distinct from the stocktake-driven adjustments silo/counts.ts already posts.
// Four-state lifecycle (DRAFT|POSTED|REVERSED|CANCELLED) — declared locally, same
// out-of-scope-for-this-goal reasoning as STORES_ISSUE_STATUSES/TRANSFER_STATUSES
// above (enums.ts is G2's foundation file).
// ---------------------------------------------------------------------------
export const STOCK_ADJUSTMENT_STATUSES = ['DRAFT', 'POSTED', 'REVERSED', 'CANCELLED'];
/** One adjustment line: a signed qty delta (positive adds stock, negative removes
 * it) at a product×location, optionally paired with a handling-units (cage/crate)
 * delta. `quantitySchema` (common.ts) already permits negative/zero — unlike
 * issues/transfers, an adjustment line is NOT always positive. The refine mirrors
 * the DB CHECK (`stock_adjustment_lines_not_all_zero`) at the API layer so a
 * both-zero line 400s with a clear message instead of surfacing as a raw
 * constraint-violation error. */
export const stockAdjustmentLineInputSchema = z
    .object({
    productId: ulidSchema,
    locationId: ulidSchema,
    qtyDelta: quantitySchema,
    handlingUnitsDelta: z.number().int().nullable().optional(),
    note: z.string().nullable().optional(),
})
    .refine((line) => Number(line.qtyDelta) !== 0 || (line.handlingUnitsDelta ?? 0) !== 0, {
    message: 'Each line must change quantity or handling units (not both zero)',
    path: ['qtyDelta'],
});
export const stockAdjustmentWriteSchema = z.object({
    siteId: ulidSchema,
    // API-edge rejection (adversarial gate, 2026-08-21): compares against the TRIMMED
    // value so padded variants (' wac-residual-flush ') are also caught — free text
    // here otherwise reaches the ledger as a user-forgeable stand-in for the
    // system-only `wac-residual-flush` marker (see WAC_RESIDUAL_FLUSH_REASON's doc).
    // This turns the accident/malicious path into a clean 400 at draft creation
    // instead of a late ledger error or a silently-skipped fold.
    reason: z
        .string()
        .min(1, 'Reason is required')
        .refine((v) => v.trim() !== WAC_RESIDUAL_FLUSH_REASON, {
        message: 'This reason is reserved for system-generated ledger rows',
    }),
    notes: z.string().nullable().optional(),
    lines: z.array(stockAdjustmentLineInputSchema).min(1, 'At least one line is required'),
});
// Edit (PATCH, DRAFT-only) accepts the exact same shape as create — a full
// replace of the header + lines, matching "documents are freely editable in
// DRAFT" (CLAUDE.md conventions).
export const stockAdjustmentUpdateSchema = stockAdjustmentWriteSchema;
export const stockAdjustmentLineResponseSchema = z.object({
    id: ulidSchema,
    lineNo: z.number().int().positive(),
    productId: ulidSchema,
    locationId: ulidSchema,
    qtyDelta: z.string(),
    handlingUnitsDelta: z.number().int().nullable(),
    note: z.string().nullable(),
});
export const stockAdjustmentResponseSchema = z.object({
    id: ulidSchema,
    tenantId: ulidSchema,
    siteId: ulidSchema,
    number: z.string(),
    status: z.enum(STOCK_ADJUSTMENT_STATUSES),
    reason: z.string(),
    notes: z.string().nullable(),
    lines: z.array(stockAdjustmentLineResponseSchema),
});
export const stockAdjustmentListQuerySchema = paginationRequestSchema;
/** One `recordMovement` result surfaced back to the caller as part of a posting
 * summary — `valueCents` transported as a decimal string (JSON has no bigint),
 * mirroring `storesIssueMovementSummarySchema`. */
export const stockAdjustmentMovementSummarySchema = z.object({
    lineId: ulidSchema,
    productId: ulidSchema,
    qtyDelta: z.string(),
    valueCents: z.string(),
    movementId: ulidSchema,
});
export const stockAdjustmentPostResponseSchema = z.object({
    adjustment: stockAdjustmentResponseSchema,
    movements: z.array(stockAdjustmentMovementSummarySchema),
    summary: z.string(),
});
export const stockAdjustmentReverseResponseSchema = z.object({
    adjustment: stockAdjustmentResponseSchema,
    reversedMovementIds: z.array(ulidSchema),
});
//# sourceMappingURL=stores.js.map